import {
  DndContext,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  useDraggable,
  useDroppable
} from "@dnd-kit/core";


import { useState, useEffect, useRef } from "react";
import { useAuth } from "../../context/AuthContext";

import {
  MessageSquare,
  LayoutGrid,
  FolderKanban,
  KanbanSquare,
  ListTodo,
  BarChart3,
  Users,
  Settings,
  Eye,
  Star,
  Share2,
  MoreHorizontal,
  X,
  CheckSquare,
  Paperclip,
  ChevronDown,
  Plus,
  ExternalLink,
  Smile,
  Paperclip as AttachmentIcon,
  FileText
} from "lucide-react";


/* ================= BOARD ================= */
const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

export default function Board() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<any[]>([]);
  const [selectedIssue, setSelectedIssue] = useState<any>(null);

  const goTo = (page: string) => {
    window.location.hash = page;
  };

  const fetchTasks = () => {
    fetch(`${API_BASE}/get_issues.php`)
      .then(res => res.json())
      .then(data => setTasks(data));
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor)
  );

  const addIssue = async (status: string) => {

    const summary = prompt("Enter issue summary");

    if (!summary) return;

    const issueData = {
      type: "T",
      summary: summary,
      status: status,
      priority: "medium",
      assignee: "Admin",
      due_date: new Date().toISOString().split("T")[0],
    };

    try {

      const res = await fetch(
        `${API_BASE}/create_issue.php`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(issueData),
        }
      );

      const data = await res.json();

      if (data.success) {
        alert("Issue created successfully");
        fetchTasks();   // correct function name
      } else {
        alert(data.message);
      }

    } catch (error) {
      console.error("Create Issue Error:", error);
    }

  };


  const deleteIssue = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm("Are you sure you want to delete this issue?")) return;

    await fetch(`${API_BASE}/delete_issue.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id })
    });
    fetchTasks();
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    const taskId = active.id;
    const newStatus = over.id;

    const draggedTask = tasks.find(t => t.id == taskId);
    if (!draggedTask || draggedTask.status === newStatus) return;

    fetch(`${API_BASE}/update_issue_status.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: taskId,
        status: newStatus
      })
    })
      .then(res => res.json())
      .then(() => {
        fetchTasks();
      });
  };


  const backlog = tasks.filter(t => t.status === "backlog");
  const todo = tasks.filter(t => t.status === "todo");
  const inprogress = tasks.filter(t => t.status === "in progress");
  const review = tasks.filter(t => t.status === "review");
  const done = tasks.filter(t => t.status === "done");

  return (
    <div className="flex flex-col flex-1 bg-[#f6f7fb]">

      {/* BODY */}
      <div className="flex flex-1">

        {/* SIDEBAR */}
        <aside className="w-64 shrink-0 bg-white border-r px-5 py-6 space-y-8 shadow-sm">
          <div>
            <h2 className="text-lg font-bold text-green-600">X2INFOTECH</h2>
            <p className="text-xs text-gray-400">Project Dashboard</p>
          </div>

          <div className="space-y-2">
            {(user?.role === 'admin' ||
              user?.role === 'hr' ||
              user?.role === 'product_manager' ||
              user?.role === 'scrum_master') ? (
              <>
                <Sidebar label="Dashboard" icon={LayoutGrid} onClick={() => goTo("dashboard")} />
                <Sidebar label="Projects" icon={FolderKanban} onClick={() => goTo("projects")} />
                <Sidebar label="Board" icon={KanbanSquare} onClick={() => goTo("board")} />
                <Sidebar label="Tasks" icon={ListTodo} onClick={() => goTo("issues")} />
                <Sidebar label="Reports" icon={BarChart3} onClick={() => goTo("reports")} />
                <Sidebar label="Team" icon={Users} onClick={() => goTo("team")} />
                <Sidebar label="Settings" icon={Settings} onClick={() => goTo("settings")} />
              </>
            ) : (
              <>
                <Sidebar label="Main Dashboard" icon={LayoutGrid} onClick={() => goTo("dashboard")} />
                <Sidebar label="Board" icon={KanbanSquare} onClick={() => goTo("board")} />
              </>
            )}
          </div>
        </aside>

        {/* MAIN */}
        <main className="flex-1 px-8 py-6 overflow-x-auto">

          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragEnd={handleDragEnd}
          >
            <div className="flex gap-6 min-w-[1300px]">

              <Column title="Backlog" onAdd={addIssue}>
                {backlog.map(task => (
                  <Issue key={task.id} {...task} setSelectedIssue={setSelectedIssue} deleteIssue={deleteIssue} />
                ))}
              </Column>

              <Column title="To Do" onAdd={addIssue}>
                {todo.map(task => (
                  <Issue key={task.id} {...task} setSelectedIssue={setSelectedIssue} deleteIssue={deleteIssue} />
                ))}
              </Column>

              <Column title="In Progress" onAdd={addIssue}>
                {inprogress.map(task => (
                  <Issue key={task.id} {...task} setSelectedIssue={setSelectedIssue} deleteIssue={deleteIssue} />
                ))}
              </Column>

              <Column title="In Review" onAdd={addIssue}>
                {review.map(task => (
                  <Issue key={task.id} {...task} setSelectedIssue={setSelectedIssue} />
                ))}
              </Column>

              <Column title="Done" >
                {done.map(task => (
                  <Issue key={task.id} {...task} setSelectedIssue={setSelectedIssue} />
                ))}
              </Column>

            </div>
          </DndContext>
        </main>

      </div>



      {/* POPUP MODAL */}
      {selectedIssue && (
        <IssueModal
          issue={selectedIssue}
          onClose={() => setSelectedIssue(null)}
          onUpdate={fetchTasks}
          user={user}
        />
      )}
    </div>
  );
}

/* ================= COMPONENTS ================= */

function Sidebar({ label, icon: Icon, onClick }: any) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2 rounded-md transition text-gray-700 hover:bg-gray-100"
    >
      <Icon size={16} />
      <span>{label}</span>
    </button>
  );
}

function Column({ title, children }: any) {

  const statusMap: any = {
    "Backlog": "backlog",
    "To Do": "todo",
    "In Progress": "in progress",
    "In Review": "review",
    "Done": "done"
  };

  const status = statusMap[title];

  const { setNodeRef } = useDroppable({
    id: status
  });

  return (
    <div
      ref={setNodeRef}
      className="w-72 bg-[#f1f3f7] rounded-xl p-4 min-h-[500px] shadow-inner"
    >
      <h3 className="text-sm font-semibold mb-3">{title}</h3>

      <div className="space-y-3 mb-3">
        {children}
      </div>

      {/* + Add Issue button removed */}

    </div>
  );
}

function Issue({ setSelectedIssue, deleteIssue, ...task }: any) {

  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: task.id
  });

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined
  };

  const priorityColor: any = {
    low: "bg-blue-100 text-blue-600",
    medium: "bg-yellow-100 text-yellow-700",
    high: "bg-orange-100 text-orange-700",
    highest: "bg-red-100 text-red-700",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white rounded-xl p-4 shadow-sm border text-sm hover:shadow-lg transition-all duration-200"
      onClick={() => setSelectedIssue(task)}
    >
      <div className="flex justify-between items-start mb-1">
        {/* 🔥 Drag Handle */}
        <div
          {...listeners}
          {...attributes}
          className="cursor-grab active:cursor-grabbing text-gray-400 text-xs"
        >
          ⋮⋮
        </div>
      </div>

      <p className="text-xs text-gray-400 mb-1">{task.id}</p>
      <p className="font-medium mb-2">{task.summary}</p>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className={`text-xs px-2 py-0.5 rounded ${priorityColor[task.priority]}`}>
            {task.priority}
          </span>
          {task.comment_count > 0 && (
            <div className="flex items-center gap-1 text-dark-400">
              <MessageSquare size={13} />
              <span className="text-xs font-medium">{task.comment_count}</span>
            </div>
          )}
        </div>
        <Avatar small>{task.assignee?.charAt(0) || "A"}</Avatar>
      </div>
    </div>
  );
}



function Avatar({ children, small }: any) {
  return (
    <div
      className={`flex items-center justify-center rounded-full bg-green-500 text-white
      ${small ? "w-6 h-6 text-xs" : "w-8 h-8 text-sm"}`}
    >
      {children}
    </div>
  );
}

function IssueModal({ issue, onClose, onUpdate, user }: any) {
  const [description, setDescription] = useState(issue.description || "");
  const [summary, setSummary] = useState(issue.summary || "");
  const [isEditingDesc, setIsEditingDesc] = useState(false);
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [comments, setComments] = useState<any[]>([]);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [newComment, setNewComment] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchComments = () => {
    fetch(`http://localhost/x2infotech_full_integrated_project/api/get_comments.php?issue_id=${issue.id}`)
      .then(res => res.json())
      .then(data => setComments(Array.isArray(data) ? data : []));
  };

  const fetchAttachments = () => {
    fetch(`http://localhost/x2infotech_full_integrated_project/api/get_issue_attachments.php?issue_id=${issue.id}`)
      .then(res => res.json())
      .then(data => setAttachments(Array.isArray(data) ? data : []));
  };

  useEffect(() => {
    fetchComments();
    fetchAttachments();
  }, [issue.id]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("issue_id", issue.id);

    // Get token for auth
    const token = localStorage.getItem("token");

    const res = await fetch(`${API_BASE}/add_issue_attachment.php`, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${token}`
      },
      body: formData
    });

    const result = await res.json();
    if (result.status) {
      fetchAttachments();
    } else {
      alert("Upload failed: " + result.message);
    }
  };

  const handleSaveDescription = async () => {
    await fetch(`${API_BASE}/update_issue_details.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: issue.id,
        description: description
      })
    });
    setIsEditingDesc(false);
    onUpdate();
  };

  const handleSaveSummary = async () => {
    if (!summary.trim()) return setIsEditingSummary(false);
    await fetch(`${API_BASE}/update_issue_details.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: issue.id,
        summary: summary
      })
    });
    setIsEditingSummary(false);
    onUpdate();
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    await fetch(`${API_BASE}/add_comment.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        issue_id: issue.id,
        comment: newComment,
        user_name: user?.name || "Anonymous"
      })
    });
    setNewComment("");
    fetchComments();
  };

  const handleStatusChange = async (newStatus: string) => {
    await fetch(`${API_BASE}/update_issue_status.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: issue.id,
        status: newStatus
      })
    });
    onUpdate();
    onClose();
  };

  const getPriorityIcon = (p: string) => {
    const colors: any = {
      highest: "text-red-600",
      high: "text-orange-600",
      medium: "text-yellow-600",
      low: "text-blue-600"
    };
    return <span className={`inline-block w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[10px] border-b-current ${colors[p] || "text-gray-400"}`}></span>;
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4" onClick={onClose}>
      <div
        className="bg-white w-full max-w-6xl h-[90vh] rounded-xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* TOP TOOLBAR */}
        <div className="px-6 py-3 border-b flex items-center justify-between bg-white shrink-0">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5 text-slate-500 hover:bg-slate-100 px-2 py-1 rounded transition cursor-pointer">
              <KanbanSquare size={16} className="text-blue-600" />
              <span className="text-xs font-medium uppercase tracking-wider">{issue.id || "WEB-101"}</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <ToolbarButton icon={Eye} />
            <ToolbarButton icon={Star} />
            <ToolbarButton icon={Share2} />
            <ToolbarButton icon={MoreHorizontal} />
            <div className="w-px h-6 bg-slate-200 mx-2"></div>
            <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500 transition">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* MODAL BODY */}
        <div className="flex-1 flex overflow-hidden">
          {/* LEFT CONTENT */}
          <div className="flex-1 overflow-y-auto px-10 py-8 custom-scrollbar">
            {/* Title / Summary */}
            <div className="mb-6 group">
              {isEditingSummary ? (
                <textarea
                  autoFocus
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  onBlur={handleSaveSummary}
                  className="w-full text-2xl font-semibold bg-white border-2 border-blue-500 rounded px-2 py-1 focus:outline-none resize-none"
                  rows={2}
                />
              ) : (
                <h1
                  onClick={() => setIsEditingSummary(true)}
                  className="text-2xl font-semibold text-slate-800 leading-tight hover:bg-slate-50 px-2 -mx-2 py-1 rounded transition cursor-text"
                >
                  {summary}
                </h1>
              )}
            </div>

            {/* Quick Actions Bar */}
            <div className="flex items-center gap-3 mb-8">
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                onChange={handleFileUpload}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded font-medium text-slate-700 text-sm transition"
              >
                <Paperclip size={16} /> Attach
              </button>
              <button className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded font-medium text-slate-700 text-sm transition">
                <CheckSquare size={16} /> Create subtask
              </button>
              <button className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded font-medium text-slate-700 text-sm transition">
                <ExternalLink size={16} /> Link issue
              </button>
            </div>

            {/* Description Section */}
            <div className="mb-10">
              <h3 className="text-sm font-bold text-slate-700 mb-3">Description</h3>
              {isEditingDesc ? (
                <div className="space-y-3">
                  <textarea
                    autoFocus
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full min-h-[150px] border-2 border-blue-500 rounded-lg p-4 text-sm focus:outline-none resize-none"
                    placeholder="Add a more detailed description..."
                  />
                  <div className="flex gap-2">
                    <button onClick={handleSaveDescription} className="bg-blue-600 text-white px-4 py-1.5 rounded font-medium text-sm hover:bg-blue-700 transition">Save</button>
                    <button onClick={() => { setDescription(issue.description || ""); setIsEditingDesc(false); }} className="text-slate-600 px-4 py-1.5 rounded font-medium text-sm hover:bg-slate-100 transition">Cancel</button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => setIsEditingDesc(true)}
                  className="p-4 border border-transparent hover:bg-slate-50 hover:border-slate-200 rounded-lg min-h-[80px] cursor-text transition text-sm text-slate-700 whitespace-pre-wrap"
                >
                  {description || <span className="text-slate-400 italic">Add a description...</span>}
                </div>
              )}
            </div>

            {/* Attachments Section */}
            <div className="mb-10">
              <h3 className="text-sm font-bold text-slate-700 mb-3 uppercase tracking-wider text-[11px]">Attachments ({attachments.length})</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {attachments.map((att) => (
                  <AttachmentCard
                    key={att.attachment_id}
                    name={att.original_name}
                    info={`${(att.size / 1024).toFixed(1)} KB • ${new Date(att.created_at).toLocaleDateString()}`}
                    type={att.mime.includes('image') ? 'image' : 'file'}
                    url={att.url}
                  />
                ))}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-slate-200 rounded-lg p-4 flex flex-col items-center justify-center hover:bg-slate-50 transition cursor-pointer group min-h-[140px]"
                >
                  <Plus className="text-slate-300 group-hover:text-slate-500" size={24} />
                  <span className="text-[10px] text-slate-400 mt-2 font-medium">Add more</span>
                </div>
              </div>
            </div>

            {/* Activity Section */}
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-700">Activity</h3>
                <div className="flex items-center gap-4 text-xs font-medium text-slate-500">
                  <span>Show:</span>
                  <button className="text-blue-600 bg-blue-50 px-2 py-0.5 rounded">All</button>
                  <button className="hover:text-slate-800">Comments</button>
                  <button className="hover:text-slate-800">History</button>
                </div>
              </div>

              {/* Comment Input */}
              <div className="flex gap-4">
                <Avatar>{user?.name?.charAt(0) || "U"}</Avatar>
                <div className="flex-1 group">
                  <div className="relative">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      className="w-full border-2 border-slate-200 focus:border-blue-500 rounded-xl p-4 text-sm outline-none transition-all resize-none"
                      placeholder="Add a comment... (Type M for markdown)"
                      rows={2}
                    />
                    <div className="absolute bottom-3 right-4 flex items-center gap-2 opacity-0 group-focus-within:opacity-100 transition-opacity">
                      <Smile size={18} className="text-slate-400 hover:text-slate-600 cursor-pointer" />
                      <AttachmentIcon size={18} className="text-slate-400 hover:text-slate-600 cursor-pointer" />
                    </div>
                  </div>
                  <div className="mt-3 hidden group-focus-within:flex gap-2">
                    <button onClick={handleAddComment} className="bg-blue-600 text-white px-4 py-1.5 rounded-lg font-medium text-sm hover:bg-blue-700 transition">Save</button>
                    <button onClick={() => setNewComment("")} className="text-slate-600 px-4 py-1.5 rounded-lg font-medium text-sm hover:bg-slate-100 transition">Cancel</button>
                  </div>
                </div>
              </div>

              {/* Comments Feed */}
              <div className="space-y-6">
                {comments.length > 0 ? comments.map((c) => (
                  <div key={c.id} className="flex gap-4 group">
                    <Avatar>{c.user_name?.charAt(0) || "A"}</Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-bold text-slate-800">{c.user_name}</span>
                        <span className="text-xs text-slate-400">
                          {new Date(c.created_at).toLocaleDateString()} at {new Date(c.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <div className="bg-slate-50 p-4 rounded-xl rounded-tl-none border border-slate-100 text-sm text-slate-700">
                        {c.comment}
                      </div>
                      <div className="flex gap-3 mt-2 px-1 text-xs font-semibold text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button className="hover:text-slate-600">Edit</button>
                        <span>•</span>
                        <button className="hover:text-slate-600">Reply</button>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-6 text-slate-400 text-sm italic">No comments yet.</div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT SIDEBAR */}
          <div className="w-80 border-l bg-slate-50/50 p-8 flex flex-col space-y-8 overflow-y-auto">
            <div>
              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-3 block">Status</label>
              <div className="relative group">
                <select
                  value={issue.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className="w-full appearance-none bg-blue-600 hover:bg-blue-700 text-white rounded px-4 py-2 font-bold text-xs uppercase tracking-wider transition cursor-pointer pr-10"
                >
                  <option value="backlog">Backlog</option>
                  <option value="todo">To Do</option>
                  <option value="in progress">In Progress</option>
                  <option value="review">In Review</option>
                  <option value="done">Done</option>
                </select>
                <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-white pointer-events-none" />
              </div>
            </div>

            <div className="space-y-6">
              <DetailRow label="Assignee">
                <div className="flex items-center gap-3 hover:bg-slate-100 p-1 -m-1 rounded transition cursor-pointer group">
                  <Avatar small>{issue.assignee?.charAt(0) || "U"}</Avatar>
                  <span className="text-sm font-medium text-slate-700 group-hover:text-blue-600">{issue.assignee || "Unassigned"}</span>
                </div>
              </DetailRow>

              <DetailRow label="Reporter">
                <div className="flex items-center gap-3 p-1 -m-1">
                  <Avatar small>{user?.name?.charAt(0) || "SM"}</Avatar>
                  <span className="text-sm font-medium text-slate-700">{user?.name || "Sarah Miller"}</span>
                </div>
              </DetailRow>

              <DetailRow label="Priority">
                <div className="flex items-center gap-2 hover:bg-slate-100 p-1 -m-1 rounded transition cursor-pointer">
                  {getPriorityIcon(issue.priority)}
                  <span className="text-sm font-medium text-slate-700 capitalize">{issue.priority}</span>
                </div>
              </DetailRow>

              <DetailRow label="Labels">
                <div className="flex flex-wrap gap-1.5">
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded uppercase tracking-wider">frontend</span>
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded uppercase tracking-wider">design</span>
                  <span className="p-1 hover:bg-slate-200 rounded text-slate-400 cursor-pointer transition">
                    <Plus size={14} />
                  </span>
                </div>
              </DetailRow>

              <DetailRow label="Sprint">
                <div className="flex items-center gap-2 text-blue-600 hover:underline cursor-pointer text-sm font-medium">
                  Sprint 2 (Active)
                </div>
              </DetailRow>

              <DetailRow label="Story Points">
                <div className="bg-slate-200 hover:bg-slate-300 w-10 text-center rounded py-0.5 font-bold text-sm text-slate-700 transition cursor-pointer">
                  5
                </div>
              </DetailRow>
            </div>

            <div className="pt-6 border-t border-slate-200 space-y-2">
              <p className="text-[11px] text-slate-400 flex items-center justify-between">
                <span>Created:</span>
                <span className="font-medium text-slate-500">{new Date(issue.created_at).toLocaleDateString()}</span>
              </p>
              <p className="text-[11px] text-slate-400 flex items-center justify-between">
                <span>Updated:</span>
                <span className="font-medium text-slate-500">{new Date(issue.updated_at).toLocaleDateString()}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ToolbarButton({ icon: Icon }: { icon: any }) {
  return (
    <button className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 transition group">
      <Icon size={18} className="group-hover:text-slate-800" />
    </button>
  );
}

function DetailRow({ label, children }: { label: string, children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-bold text-slate-400 uppercase tracking-widest text-[10px]">{label}</label>
      {children}
    </div>
  );
}

function AttachmentCard({ name, info, type, url }: { name: string, info: string, type: string, url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex flex-col border border-slate-200 rounded-lg overflow-hidden group hover:shadow-md transition cursor-pointer"
    >
      <div className="h-24 bg-slate-100 flex items-center justify-center relative overflow-hidden">
        {type === 'image' ? (
          <img src={url} alt={name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <div className="flex flex-col items-center gap-1">
            <FileText className="text-slate-300 h-10 w-10 group-hover:scale-110 transition-transform" />
            <span className="text-[9px] font-bold text-slate-400 uppercase">{name.split('.').pop()}</span>
          </div>
        )}
      </div>
      <div className="p-3 border-t bg-white">
        <p className="text-xs font-bold text-slate-800 truncate mb-1">{name}</p>
        <p className="text-[10px] text-slate-400">{info}</p>
      </div>
    </a>
  );
}